/**
 * CreateTenantDialog — Süperadmin yeni merchant açılış modal'ı.
 *
 * Backend: POST /admin/tenants
 * Backend plan katalogu: GET /admin/plans → dört plan (trial, monthly, yearly,
 * two_year). Fiyat/süre backend sabiti — frontend sadece göstergeci.
 */
import { useEffect, useState } from "react";
import { Loader2, Building2, Globe, UserRound, Mail, Package } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PLAN_META, readError } from "./_tenant-helpers";
import PlanPriceBlock from "./PlanPriceBlock";

export default function CreateTenantDialog({ open, onOpenChange, onCreated }) {
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plan, setPlan] = useState("trial");

    const [company, setCompany] = useState("");
    const [domain, setDomain] = useState("");
    const [ownerName, setOwnerName] = useState("");
    const [ownerEmail, setOwnerEmail] = useState("");
    const [busy, setBusy] = useState(false);

    // Dialog açıldığında planları yükle + form reset
    useEffect(() => {
        if (!open) return;
        setCompany(""); setDomain(""); setOwnerName(""); setOwnerEmail("");
        setPlan("trial");
        setPlansLoading(true);
        adminApi.get("/admin/plans")
            .then(({ data }) => setPlans(data?.plans || []))
            .catch(() => setPlans([]))
            .finally(() => setPlansLoading(false));
    }, [open]);

    const canSubmit =
        company.trim() && domain.trim() && ownerName.trim() &&
        ownerEmail.trim().includes("@") && plan && !busy;

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSubmit) return;
        setBusy(true);
        try {
            const { data } = await adminApi.post("/admin/tenants", {
                company_name: company.trim(),
                site_domain: domain.trim(),
                owner_name: ownerName.trim(),
                owner_email: ownerEmail.trim().toLowerCase(),
                plan,
            });
            toast.success(`${data.company_name} hesabı açıldı.`);
            onCreated?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(readError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[720px]" data-testid="admin-create-tenant-dialog">
                <DialogHeader>
                    <DialogTitle>Yeni Merchant Aç</DialogTitle>
                    <DialogDescription>
                        Hesap bilgilerini gir, paketi seç. Hesap anında aktifleşir;
                        sahip e-postaya ilk giriş için OTP ile ulaşır.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Hesap bilgileri — iki kolon */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                            label="Firma Adı"
                            icon={Building2}
                            value={company}
                            onChange={setCompany}
                            placeholder="Örn: Boostex"
                            testId="create-tenant-company"
                        />
                        <FormField
                            label="Alan Adı"
                            icon={Globe}
                            value={domain}
                            onChange={setDomain}
                            placeholder="Örn: boostex.com"
                            testId="create-tenant-domain"
                        />
                        <FormField
                            label="Sahip Adı"
                            icon={UserRound}
                            value={ownerName}
                            onChange={setOwnerName}
                            placeholder="Örn: Ahmet Yılmaz"
                            testId="create-tenant-owner-name"
                        />
                        <FormField
                            label="Sahip E-posta"
                            icon={Mail}
                            value={ownerEmail}
                            onChange={setOwnerEmail}
                            placeholder="owner@boostex.com"
                            type="email"
                            testId="create-tenant-owner-email"
                        />
                    </div>

                    {/* Plan seçici — tam genişlik, 4 kolonda kartlar */}
                    <div className="space-y-2.5">
                        <div className="flex items-center gap-2">
                            <Package className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                            <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                                Paket Seç
                            </span>
                        </div>
                        {plansLoading ? (
                            <div className="h-24 flex items-center justify-center text-xs text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Paketler yükleniyor…
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="create-tenant-plans">
                                {plans.map((p) => (
                                    <PlanCard
                                        key={p.key}
                                        plan={p}
                                        selected={plan === p.key}
                                        onClick={() => setPlan(p.key)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={busy}
                            className="h-9"
                            data-testid="create-tenant-cancel"
                        >
                            Vazgeç
                        </Button>
                        <Button
                            type="submit"
                            disabled={!canSubmit}
                            className="h-9 bg-foreground text-background hover:bg-foreground/90"
                            data-testid="create-tenant-submit"
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                            Hesabı Aç
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function FormField({ label, icon: Icon, value, onChange, placeholder, type = "text", testId }) {
    return (
        <div className="space-y-1.5">
            <label className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground flex items-center gap-1.5">
                <Icon className="h-3 w-3" strokeWidth={1.75} />
                {label}
            </label>
            <Input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="h-10 text-sm"
                data-testid={testId}
            />
        </div>
    );
}

function PlanCard({ plan, selected, onClick }) {
    const meta = PLAN_META[plan.key] || PLAN_META.trial;
    const totalDays = plan.days + (plan.bonus_days || 0);
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`plan-card-${plan.key}`}
            data-selected={selected ? "true" : "false"}
            className={[
                "text-left rounded-lg border p-3 transition-all",
                selected
                    ? "border-foreground bg-foreground/[0.03] shadow-sm"
                    : "border-border bg-background hover:border-foreground/30",
            ].join(" ")}
        >
            <div className="flex items-center justify-between mb-1.5">
                <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${meta.tone}`}>
                    <span className={`h-1 w-1 rounded-full ${meta.dot}`} />
                    {meta.label}
                </span>
                {selected && (
                    <span className="h-2 w-2 rounded-full bg-foreground" aria-hidden />
                )}
            </div>
            <PlanPriceBlock plan={plan} testId={`plan-card-${plan.key}-price`} />
            <div className="text-[11px] text-muted-foreground leading-tight mt-1.5">
                {plan.days} gün{plan.bonus_days ? ` + ${plan.bonus_days} bonus` : ""}
            </div>
            <div className="text-[10px] text-muted-foreground/80 mt-0.5">
                Toplam {totalDays} gün
            </div>
        </button>
    );
}
